import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useStore } from 'zustand';

import {
  ActionButton,
  breakpoints,
  ButtonModel,
  Text,
  Toaster,
} from '@notino/react-styleguide';

import { publishUpdateWishlistCountEvent } from '@components/Universals/WishlistModifier/useWishlistActions';
import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import { WISHLIST_BASE_URL } from '@constants';
import { useWishlistId } from '@containers/WishList/hooks/useWishlistId';
import {
  useListDataCache,
  useListsData,
} from '@containers/WishlistLists/hooks/useListsData';
import { trackDeleteList } from '@containers/WishlistLists/tracking';
import { useWishlistSpaNavigate } from '@containers/wishlistShared/useWishlistSpaNavigate';
import { getWishlistTotalCount } from '@lib/wishlist/getWishlistTotalCount';
import { styled } from '@linaria/react';

import { useCloseListMenuDrawer, useListMenuContext } from '../context';
import messages, { listNotificationMessages } from '../messages';

const Wrapper = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  padding-bottom: 2rem;
  @media (min-width: ${breakpoints.md}) {
    padding-bottom: 0;
  }
`;

const Buttons = styled.div`
  width: 100%;
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  > * {
    width: 100%;
  }
`;

export const Delete = () => {
  const wishlistSpaNavigate = useWishlistSpaNavigate();
  const { store } = useListMenuContext();
  const { list: _list } = useStore(store);
  const list = _list!;
  const onClose = useCloseListMenuDrawer();
  const isWishlistDetailPage = Boolean(useWishlistId().listId);

  const { lists } = useListsData();

  const { deleteList } = useWishlistLists();
  const { deleteListCache } = useListDataCache();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  const onDelete = async () => {
    setError(false);
    setLoading(true);
    try {
      await deleteList({ id: list.id });
      deleteListCache(list.id);
      trackDeleteList({
        interaction: isWishlistDetailPage ? 'wishlist_detail' : 'wishlist_list',
        wishlistTotalLists: lists.length - 1,
        wishlistTotalProducts: getWishlistTotalCount({
          lists,
          removeFromLists: [{ id: list.id }],
          webIds: list.products ?? [],
        }),
      });
      onClose();
      Toaster.info({
        icon: 'IconRegularCheckmark',
        content: <FormattedMessage {...listNotificationMessages.listDeleted} />,
      });
      wishlistSpaNavigate(WISHLIST_BASE_URL);
      publishUpdateWishlistCountEvent();
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  };

  return (
    <Wrapper>
      <Text typography="bodyRegular" color="text.secondary">
        <FormattedMessage {...messages.deleteDrawerText} />
      </Text>

      <Footer>
        <Buttons>
          <ActionButton onClick={onDelete} isLoading={loading} error={error}>
            <FormattedMessage {...messages.deleteDrawerYesButton} />
          </ActionButton>

          <ActionButton
            buttonStyle={ButtonModel.Styles.secondary}
            onClick={() => store.setState({ currentAction: 'rootMenu' })}
          >
            <FormattedMessage {...messages.deleteDrawerNoButton} />
          </ActionButton>
        </Buttons>
      </Footer>
    </Wrapper>
  );
};
